import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { User, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  findUserByAuthUid,
  findUserByEmail,
  findUserByPhone,
} from '../api/users';

// Roles alignés avec les constantes du projet Flutter (voir AppConstants.roleAdmin, etc.).
export type Role =
  | 'admin'
  | 'cap'
  | 'responsible'
  | 'teacher'
  | 'cgs'
  | 'drh'
  | 'UNKNOWN';

interface AuthContextValue {
  user: User | null;
  role: Role;
  loading: boolean;
  signOutUser: () => Promise<void>;
  displayName: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role>('UNKNOWN');
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState<string>('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setDisplayName('');

      if (firebaseUser) {
        try {
          let userRef = doc(db, 'users', firebaseUser.uid);
          const snap = await getDoc(userRef);
          let data = snap.data() as
            | {
                role?: string;
                firstName?: string;
                lastName?: string;
                authUid?: string;
              }
            | undefined;

          // 1) Fallback by authUid field.
          if (!data) {
            const linkedUser = await findUserByAuthUid(firebaseUser.uid);
            if (linkedUser) {
              userRef = doc(db, 'users', linkedUser.id);
              data = {
                role: linkedUser.role,
                firstName: linkedUser.firstName,
                lastName: linkedUser.lastName,
                authUid: linkedUser.authUid,
              };
            }
          }

          // 2) Fallback by email if not found.
          if (!data && firebaseUser.email) {
            const byEmail = await findUserByEmail(firebaseUser.email);
            if (byEmail) {
              userRef = doc(db, 'users', byEmail.id);
              data = {
                role: byEmail.role,
                firstName: byEmail.firstName,
                lastName: byEmail.lastName,
                authUid: byEmail.authUid,
              };
            }
          }

          // 3) Fallback by phone if still not found.
          if (!data && firebaseUser.phoneNumber) {
            const byPhone = await findUserByPhone(firebaseUser.phoneNumber);
            if (byPhone) {
              userRef = doc(db, 'users', byPhone.id);
              data = {
                role: byPhone.role,
                firstName: byPhone.firstName,
                lastName: byPhone.lastName,
                authUid: byPhone.authUid,
              };
            }
          }

          // Link user doc to current Firebase Auth UID if needed.
          if (data && data.authUid !== firebaseUser.uid) {
            await updateDoc(userRef, { authUid: firebaseUser.uid });
            data = { ...data, authUid: firebaseUser.uid };
          }

          const docRole = data?.role;

          if (
            docRole === 'admin' ||
            docRole === 'cap' ||
            docRole === 'responsible' ||
            docRole === 'teacher' ||
            docRole === 'cgs' ||
            docRole === 'drh'
          ) {
            setRole(docRole);
          } else {
            setRole('UNKNOWN');
          }

          const fullName = `${data?.firstName ?? ''} ${data?.lastName ?? ''}`
            .trim();
          if (fullName) {
            setDisplayName(fullName);
          } else if (firebaseUser.displayName) {
            setDisplayName(firebaseUser.displayName);
          } else if (firebaseUser.email) {
            setDisplayName(firebaseUser.email);
          } else {
            setDisplayName('');
          }
        } catch {
          setRole('UNKNOWN');
          setDisplayName(
            firebaseUser?.displayName || firebaseUser?.email || ''
          );
        }
      } else {
        setRole('UNKNOWN');
        setDisplayName('');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role,
      loading,
      signOutUser: () => signOut(auth),
      displayName,
    }),
    [user, role, loading, displayName]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

